import {
  CelebrusDataLayer,
  CelebrusQuote,
} from '@core/models/celebrus-data-layer.model';
import { ProductType, Role } from '@core/models/api/dsm-types';

export const CELEBRUS_DATA_LAYER_KEY = 'dataLayer';

const AGENCY_CHANNELS: Role[] = ['DIRECT', 'EA', 'IA', 'NSS'];

export function readCelebrusDataLayer(storage: Storage): CelebrusDataLayer {
  const storedValue = storage.getItem(CELEBRUS_DATA_LAYER_KEY);
  if (!storedValue) {
    return { roles: [], quotes: [] };
  }

  const parsedValue: unknown = JSON.parse(storedValue);
  if (!isRecord(parsedValue)) {
    return { roles: [], quotes: [] };
  }

  const roles = Array.isArray(parsedValue.roles)
    ? parsedValue.roles.filter(isAgencyChannel)
    : [];
  const quotes = Array.isArray(parsedValue.quotes)
    ? parsedValue.quotes.filter(isCelebrusQuote)
    : [];

  return {
    ...(typeof parsedValue.userID === 'string'
      ? { userID: parsedValue.userID }
      : {}),
    roles,
    quotes,
  };
}

export function writeCelebrusDataLayer(
  storage: Storage,
  dataLayer: CelebrusDataLayer
): void {
  storage.setItem(CELEBRUS_DATA_LAYER_KEY, JSON.stringify(dataLayer));
}

export function initializeCelebrusDataLayer(storage: Storage): void {
  let existingDataLayer: CelebrusDataLayer;
  try {
    existingDataLayer = readCelebrusDataLayer(storage);
  } catch (error) {
    console.error('Unable to parse the Celebrus data layer', error);
    existingDataLayer = { roles: [], quotes: [] };
  }

  const userID = readUserID(storage);
  const role = storage.getItem('role');

  writeCelebrusDataLayer(storage, {
    ...(userID ? { userID } : {}),
    roles: role && isAgencyChannel(role) ? [role] : [],
    quotes: existingDataLayer.quotes,
  });
}

function readUserID(storage: Storage): string | undefined {
  const jwt = storage.getItem('jwt');
  if (!jwt) {
    return undefined;
  }

  try {
    const parsedJwt: unknown = JSON.parse(jwt);
    if (isRecord(parsedJwt) && typeof parsedJwt.userId === 'string') {
      return parsedJwt.userId;
    }
  } catch (error) {
    console.error('Unable to parse the stored JWT for Celebrus', error);
  }

  return undefined;
}

function isAgencyChannel(value: unknown): value is Role {
  return (
    typeof value === 'string' && AGENCY_CHANNELS.includes(value as Role)
  );
}

function isCelebrusQuote(value: unknown): value is CelebrusQuote {
  return (
    isRecord(value) &&
    typeof value.productType === 'string' &&
    typeof value.quoteId === 'string' &&
    value.quoteId.length > 0
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function toCelebrusQuote(
  productType: ProductType,
  quoteId: string
): CelebrusQuote {
  return { productType, quoteId };
}
